import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/styles';
import { Container, Grid, useMediaQuery } from '@mui/material';
import moment from 'moment';
import Page from 'src/shared/components/Page';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { selectCompany, selectInsights } from 'src/old/actions/company/companySelectors';
import { useTranslation } from 'react-i18next';
import { fetchCompany, fetchCompanyInsights } from 'src/old/actions';
import { selectIsLoading } from 'src/old/actions/loading/loadingSelectors';
import Header from './Header';
import Overview from './Overview';
import TransfersOverTime from './TransfersOverTime';
import TransferAmounts from './TransferAmounts';
import UsageCharts from './UsageCharts';
import { formatPayrollData } from './helpers/payrolls';
import { ColumnContentWrapper, useStyles } from './styles';
import { charts } from './chartsData';
import { statistics } from './statisticsData';
import { BalanceAlert } from 'src/modules/DashboardDefault/BalanceAlert';
import { OverviewColumnContent } from './components/OverviewColumnContent';
import { ConditionalOverlay } from 'src/shared/components/ConditionalOverlay';

const DashboardDefault = () => {
  const classes = useStyles();
  const { t } = useTranslation(['overview', 'common']);
  const dispatch = useDispatch();
  const currentCompany = useSelector(selectCompany);
  const insights = useSelector(selectInsights);
  const isLoading = useSelector(selectIsLoading);
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.up('sm'));
  const spacing = isSmall ? 2 : 1;
  const finishedLoadingAllData = isLoading === false;
  const flexflowStatus = currentCompany?.flexFlow;
  const payflowStatus = currentCompany?.payflow;

  const flexflowAllowedInThisLocale = currentCompany?.locale === 'es-ES';

  useEffect(() => {
    Promise.all([fetchCompany()(dispatch), fetchCompanyInsights()(dispatch)])
      .then(() => {})
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chartsData = charts({ t, theme });
  const statisticsData = statistics({
    t,
    theme,
    currentCompany
  });

  const payrollsData = insights ? formatPayrollData(insights.paymentsByMonth) : [];
  const companyCreatedAtDate: string =
    currentCompany && moment(currentCompany?.createdAt).local().format('YYYY-MM-DD HH:m:s');
  const formatBoxTitle =
    companyCreatedAtDate &&
    `${t('common:capitalize', {
      value: t('common:since')
    })} ${companyCreatedAtDate}`;

  const overviewBoxesData = [
    {
      headline: `${t('common:no')} ${t('of registered employees')}`,
      title: formatBoxTitle,
      amount: insights?.employeeTotals?.activeCount || 0,
      icon: <AccountCircleOutlinedIcon />
    }
  ];

  if (!currentCompany) {
    return <></>;
  }

  return (
    <Page className={classes.root} title={t('overview')} dataTestId="overview-page">
      {finishedLoadingAllData && (
        <Container maxWidth={false}>
          <BalanceAlert />
          {/* @ts-expect-error: TODO: Update when refactoring <Header /> component */}
          <Header />
          <Grid container className={classes.grid}>
            <Grid container justifyContent="space-between" spacing={spacing}>
              {/* @ts-expect-error: TODO: Update when refactoring <Overview /> component */}
              <Overview data={overviewBoxesData} />
            </Grid>
            <Grid container spacing={spacing} sx={{ marginTop: spacing }}>
              <ColumnContentWrapper item md={6} sx={{ order: payflowStatus ? 1 : 2 }}>
                <ConditionalOverlay showOverlay={!payflowStatus}>
                  <OverviewColumnContent
                    chartsData={chartsData.usageChart(insights?.employeeTotals)}
                    statisticsData={statisticsData.payflowStatistics(insights?.paymentTotals)}
                    spacing={spacing}
                  />
                </ConditionalOverlay>
              </ColumnContentWrapper>

              {flexflowAllowedInThisLocale && (
                <ColumnContentWrapper item md={6} sx={{ order: payflowStatus ? 2 : 1 }}>
                  <ConditionalOverlay showOverlay={!flexflowStatus}>
                    <OverviewColumnContent
                      chartsData={chartsData.flexflowChart({
                        ...insights?.flexflowTotals,
                        disabled: Boolean(insights?.flexflowTotals)
                      })}
                      statisticsData={statisticsData.flexflowStatistics(insights?.flexflowTotals)}
                      spacing={spacing}
                    />
                  </ConditionalOverlay>
                </ColumnContentWrapper>
              )}
            </Grid>

            {payflowStatus && (
              <>
                <Grid container className={classes.grid}>
                  <Grid container justifyContent="space-between" spacing={spacing}>
                    <TransfersOverTime data={payrollsData} />
                  </Grid>
                </Grid>
                <Grid container className={classes.grid}>
                  <TransferAmounts data={payrollsData} />
                </Grid>
              </>
            )}

            <Grid container className={classes.grid}>
              <UsageCharts chartData={chartsData.saveLearnFlowChart(insights?.employeeTotals)} isSmall />
            </Grid>
          </Grid>
        </Container>
      )}
    </Page>
  );
};

export default DashboardDefault;
